/** Wording and figures for the non-delivery idle panel (VehicleIdlePanel). */
import type { VehicleDayStop, VehicleDayStopsRecord } from '../types/vehicleDayStops';
import { formatUkTime } from '../lib/calc/clock';

export function formatMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

/** "12:00–14:00 · 2 h · engine idling 20 min · 45 min tachograph break". */
export function describeIdleStop(stop: VehicleDayStop): string {
  const parts = [
    `${formatUkTime(stop.started_at)}–${formatUkTime(stop.ended_at)}${stop.open_ended ? ' (last point of the day)' : ''}`,
    formatMinutes(stop.stationary_minutes),
  ];
  if (stop.engine_idle_minutes >= 1) parts.push(`engine idling ${formatMinutes(stop.engine_idle_minutes)}`);
  if (stop.tacho_completeness === 'Unknown') {
    parts.push('tachograph not downloaded yet');
  } else if (stop.tacho_break_minutes !== undefined) {
    const unread = stop.tacho_completeness === 'Partial' && (stop.tacho_unread_minutes ?? 0) >= 1
      ? ` (${formatMinutes(stop.tacho_unread_minutes!)} not downloaded yet)`
      : '';
    parts.push(`${stop.tacho_break_minutes >= 1 ? `${formatMinutes(stop.tacho_break_minutes)} tachograph break` : 'no tachograph break'}${unread}`);
  }
  return parts.join(' · ');
}

/** The van's tachograph-break cell: break minutes, with any time the tachograph has not read yet beside them. */
export function formatTachoBreakCell(summary: VehicleDayStopsRecord['summary']): string {
  const breakMinutes = summary.non_delivery_tacho_break_minutes ?? 0;
  const unread = summary.non_delivery_tacho_unread_minutes ?? 0;
  if (unread >= 1 && unread >= summary.non_delivery_idle_minutes - 0.5) return 'not downloaded yet';
  return unread >= 1 ? `${formatMinutes(breakMinutes)} + ${formatMinutes(unread)} not downloaded` : formatMinutes(breakMinutes);
}

/** Busiest idle first, then registration — the order a planner reads. */
export function sortByIdle(records: readonly VehicleDayStopsRecord[]): VehicleDayStopsRecord[] {
  return [...records].sort((a, b) =>
    b.summary.non_delivery_idle_minutes - a.summary.non_delivery_idle_minutes
    || (a.registration ?? a.vehicle_key).localeCompare(b.registration ?? b.vehicle_key));
}

export function previousLondonDate(now: Date = new Date()): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
